import React, { useMemo, useCallback } from 'react';

import classnames from 'classnames';

import { useMediaQuery } from '@mui/material';

import GroupedDateList from 'components/uiLibrary/GroupedDateList';
import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import EntityName from 'components/Globals/EntityName';
import { usePaywallContext } from 'components/Paywall';

import { TP, DATE_FORMATS } from 'constants/index';

import ImpressionTracker from 'utils/components/impressionTracker';
import { createDate, createDatePath, getFormattedDateRange } from 'utils/date';
import useNavigate from 'utils/hooks/useNavigate';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import classes from './PerformanceDatesInfo.module.scss';

export const DateTimeTag = ({
  startDate,
  endDate,
  time,
  location,
  linkProps,
  showChevronIcon,
  skipYear = false,
  hideLinks,
  trackingData,
  venue,
  styles = {},
  onClick,
  preventDefault = true,
  isArchived,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const isMobile = useMediaQuery('(max-width: 600px)');
  const navigate = useNavigate();
  const { rangeStart, rangeEnd, separator, weekdayStart, fromISO, toISO } = useMemo(
    () => getFormattedDateRange({ from: startDate, to: endDate || startDate, asParts: true }),
    [startDate, endDate],
  );

  const { city, country } = useMemo(() => {
    if (!location) {
      return { city: undefined, country: undefined };
    }

    const parts = location
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    if (parts.length === 2) {
      return { city: parts[0], country: parts[1] };
    }

    if (parts.length === 1) {
      return { city: undefined, country: parts[0] };
    }

    return { city: undefined, country: undefined };
  }, [location]);

  if (rangeStart) {
    return (
      <LinkButton
        styles={{ root: classnames(classes.performance, styles.root) }}
        variant="native"
        {...linkProps}
        stopPropagation={!hideLinks && !!linkProps && !showChevronIcon}
        preventDefault={(hideLinks || showChevronIcon) && preventDefault}
        isLink
        onClick={onClick}
        trackingData={trackingData}
      >
        <ImpressionTracker data={trackingData} className={classes.performanceNode}>
          {skipYear ? (
            <Typography variant="p" className={classnames(classes.dateTag, styles.dateTag)}>
              <time dateTime={fromISO}>{createDate(fromISO)?.format(DATE_FORMATS.DAY_MONTH)}</time>
            </Typography>
          ) : (
            <Typography variant="p" className={classnames(classes.dateTag, styles.dateTag)}>
              <time dateTime={fromISO}>{rangeStart}</time>
              {separator}
              {rangeEnd && <time dateTime={toISO}>{rangeEnd}</time>}
            </Typography>
          )}
        </ImpressionTracker>
        <Typography variant="p" className={classnames(classes.timeTag, styles.timeTag)}>
          <Typography
            weight="medium"
            color="primary"
            align="right"
            className={classnames(classes.timeTagDay, styles.timeTagDay)}
          >
            {weekdayStart}
          </Typography>
          {time && (
            <Typography
              variant="span"
              color="secondary"
              className={classnames(classes.timeTagTime, styles.timeTagTime)}
            >
              {time}
            </Typography>
          )}
        </Typography>
        {(location || venue?.id) && (
          <Typography
            variant="div"
            weight="regular"
            color="secondary"
            truncate
            className={classnames(classes.location, styles.location)}
          >
            {isMobile ? (
              <div className={classes.locationMobile}>
                <div className={classes.locationMobile__cityCountry}>
                  <Typography size={12} weight="medium" blur={isArchived}>
                    {city}
                  </Typography>
                  <Typography size={12} blur={isArchived} color="secondary">
                    {country}
                  </Typography>
                </div>
                <div className={classes.locationMobile__venue}>
                  <SpriteIcon
                    icon="location_on_black"
                    size={12}
                    className={classnames(classes.locationIcon, styles.locationIcon)}
                  />
                  {venue?.id ? (
                    <Typography size={12} weight="medium" blur={isArchived}>
                      <EntityName entity={venue} entityType={ENTITY_TYPES.ORGANIZATION} isRaw />
                    </Typography>
                  ) : (
                    <> ({t(`${TP}.FN_VENUE_NOT_ANNOUNCED`)})</>
                  )}
                </div>
              </div>
            ) : (
              <ImpressionTracker
                as="div"
                data={{
                  ...trackingData,
                  entityId: venue?.id,
                  entityName: venue?.name || location,
                  entityType: ENTITY_TYPES.ORGANIZATION,
                }}
                className={classes.locationDesktop}
              >
                {venue?.id ? (
                  <Typography
                    size={12}
                    className={classnames(classes.desktopVenueInfo, styles.desktopVenueInfo)}
                    truncate
                    blur={isArchived}
                  >
                    <SpriteIcon
                      icon="location_on_black"
                      size={12}
                      className={classnames(classes.locationIcon, styles.locationIcon)}
                    />
                    <Typography
                      variant="div"
                      size={12}
                      className={classnames(classes.desktopVenueInfoText, styles.desktopVenueInfoText)}
                      truncate
                    >
                      <EntityName entity={venue} entityType={ENTITY_TYPES.ORGANIZATION} isRaw />
                    </Typography>
                  </Typography>
                ) : (
                  <> ({t(`${TP}.FN_VENUE_NOT_ANNOUNCED`)})</>
                )}
                <div className={classnames(classes.locationDesktop__cityCountry, styles.cityCountry)}>
                  <Typography size={12} weight="medium" blur={isArchived}>
                    {city}
                    {city && country ? ', ' : ''}
                  </Typography>
                  <Typography
                    size={12}
                    color={!city ? 'secondary' : 'primary'}
                    weight={!city ? 'medium' : 'regular'}
                    blur={isArchived}
                  >
                    {country}
                  </Typography>
                </div>
              </ImpressionTracker>
            )}
          </Typography>
        )}

        {showChevronIcon && (
          <SpriteIcon
            icon="chevron_right"
            className={classnames(classes.performance__icon, styles.performanceIcon)}
            onClick={event => {
              event.stopPropagation();
              navigate.to(linkProps);
            }}
          />
        )}
      </LinkButton>
    );
  }

  return null;
};

const PerformanceOtherDates = ({ dates, excludeDates, startYear, filterFutureDates, showCount, styles }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');

  if (!dates?.length || dates?.length === excludeDates?.length) {
    return null;
  }

  return (
    <div className={classnames(classes.performance_indent, classes.performance__allDates, styles?.allDates)}>
      <GroupedDateList
        dates={dates}
        excludeDates={excludeDates}
        startYear={startYear}
        getMoreLabel={count => `+${count} ${t(`${TP}.m_DATES`)}`}
        filterFutureDates={filterFutureDates}
        showCount={showCount}
        styles={{ groupedDateList: styles?.groupedDateList }}
      />
    </div>
  );
};

const PerformanceDatesInfo = ({ data, linkProps, onClick, hideLinks, trackingData, styles }) => {
  const { setIsPaywallExpanded } = usePaywallContext();
  const getProductionDates = useCallback(performances => performances?.map(({ startDate }) => startDate), []);
  const getExcludedDates = useCallback(dates => dates?.map(({ date }) => date), []);
  const getLastShownYear = useCallback(dates => {
    const lastDate = dates?.[dates?.length - 1]?.date;

    return createDate(lastDate).format('YYYY');
  }, []);

  const handlePerformanceClick = (e, { productionId, performanceId }) => {
    e.stopPropagation();

    if (data?.isArchived) {
      e.preventDefault();
      setIsPaywallExpanded(true);
    } else {
      onClick(e, { productionId, performanceId });
    }
  };

  if (data?.dates) {
    return (
      <div
        className={classnames(classes.performanceDatesInfo, {
          [classes.performanceDatesInfoDates]: data.dates.length < 3,
        })}
        onClick={e => {
          if (data?.isArchived) {
            e.stopPropagation();
            e.preventDefault();
            setIsPaywallExpanded(true);
          }
        }}
      >
        <div>
          {data?.dates?.map(({ id, date, time, location, title, venue }) => (
            <DateTimeTag
              key={id}
              linkProps={{
                ...linkProps?.getSubPath({
                  path: createDatePath(date),
                }),
                title,
              }}
              isArchived={data?.isArchived}
              hideLinks={hideLinks}
              startDate={date}
              endDate={date}
              time={time}
              location={location}
              venue={venue}
              onClick={e => handlePerformanceClick(e, { productionId: data.id, performanceId: id })}
              skipYear
              showChevronIcon
              trackingData={{
                ...trackingData,
                entityId: data.id, // productionId
                entityName: data.name,
                entityType: ENTITY_TYPES.PRODUCTION,
                meta: {
                  ...trackingData?.meta,
                  performanceId: id,
                  performanceDate: date,
                },
              }}
              styles={{
                root: styles?.root,
                dateTag: styles?.dateTag,
                desktopVenueInfo: styles?.desktopVenueInfo,
                cityCountry: styles?.cityCountry,
                timeTagTime: styles?.timeTagTime,
                timeTagDay: styles?.timeTagDay,
                desktopVenueInfoText: styles?.desktopVenueInfoText,
                locationIcon: styles?.locationIcon,
                groupedDateList: styles?.groupedDateList,
              }}
            />
          ))}
        </div>
        <PerformanceOtherDates
          dates={data?.upcomingDates}
          excludeDates={getExcludedDates(data?.dates)}
          startYear={getLastShownYear(data?.dates)}
          onClick={onClick}
          filterFutureDates
          showCount
          styles={{ allDates: styles?.allDates, groupedDateList: styles?.groupedDateList }}
        />
      </div>
    );
  }

  return (
    <div
      onClick={e => {
        if (data?.isArchived) {
          e.stopPropagation();
          e.preventDefault();
          setIsPaywallExpanded(true);
        }
      }}
    >
      <DateTimeTag
        linkProps={linkProps}
        startDate={data?.minDate}
        endDate={data?.maxDate}
        trackingData={{
          ...trackingData,
          entityId: data?.id,
          entityName: data?.name,
          entityType: ENTITY_TYPES.PRODUCTION,
          meta: {
            minDate: data?.minDate,
            maxDate: data?.maxDate,
          },
        }}
        isArchived={data?.isArchived}
        styles={{
          root: styles?.root,
          dateTag: styles?.dateTag,
          desktopVenueInfo: styles?.desktopVenueInfo,
          cityCountry: styles?.cityCountry,
          timeTagTime: styles?.timeTagTime,
          timeTagDay: styles?.timeTagDay,
          desktopVenueInfoText: styles?.desktopVenueInfoText,
          locationIcon: styles?.locationIcon,
          groupedDateList: styles?.groupedDateList,
        }}
      />
      <PerformanceOtherDates
        dates={getProductionDates(data?.performances)}
        onClick={onClick}
        styles={{ allDates: styles?.allDates, groupedDateList: styles?.groupedDateList }}
      />
    </div>
  );
};

export default PerformanceDatesInfo;
